import {
    CARGAR_CONFIGURACION_CALENDARIO,
    CARGAR_CONFIGURACION_CALENDARIO_EXITO,
    ACTUALIZAR_CONFIGURACION_CALENDARIO,
    ACTUALIZAR_CONFIGURACION_CALENDARIO_EXITO,
    MOSTRAR_ALERTA
} from '../types';

import clienteAxios from '../config/axios';

// Valida y normaliza la configuración visual del calendario.
// Importante: acá NO usamos default 14:00–20:00.
// Si la configuración no es válida, devolvemos null.
// El default real lo tiene que manejar el backend.
const normalizeCalendarViewSettings = (settings) => {
    if (!settings || !Array.isArray(settings.visibleTimeRanges)) {
        return null;
    }

    const visibleTimeRanges = settings.visibleTimeRanges
        .filter((range) => {
            return (
                range &&
                typeof range.start === 'string' &&
                typeof range.end === 'string' &&
                range.start <= range.end
            );
        })
        .slice(0, 2);

    if (visibleTimeRanges.length === 0) {
        return null;
    }

    return {
        visibleTimeRanges,
        autoScrollToCurrentTime: settings.autoScrollToCurrentTime !== false
    };
};

// Extrae el mensaje real del backend.
// También contempla errores creados desde el front con new Error().
const getBackendErrorMessage = (
    error,
    fallbackMessage = 'Ocurrió un error con la configuración del calendario.'
) => {
    return (
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallbackMessage
    );
};

// Carga la configuración visual del calendario desde backend.
// Esta action SIEMPRE consulta al backend cuando Calendario la llama.
// No corta por Redux, porque Redux puede venir persistido desde localStorage.
export function cargarConfiguracionCalendarioAction({ config }) {
    return async (dispatch) => {
        dispatch(cargarConfiguracionCalendario());

        try {
            const respuesta = await clienteAxios.get(
                '/usuarios/configuracioncalendario',
                config
            );

            const configuracion = normalizeCalendarViewSettings(
                respuesta?.data?.configuracion
            );

            if (!configuracion) {
                throw new Error('El backend no devolvió una configuración válida del calendario.');
            }

            dispatch(cargarConfiguracionCalendarioExito(configuracion));

            return configuracion;

        } catch (error) {
            const mensaje = getBackendErrorMessage(
                error,
                'No se pudo cargar la configuración del calendario.'
            );

            const errores = { msg: mensaje };

            dispatch(mostrarAlerta(errores));

            const customError = new Error(mensaje);
            customError.userMessage = mensaje;
            customError.originalError = error;

            throw customError;
        }
    };
}

// Guarda o actualiza la configuración visual del calendario.
// El backend toma el profesional desde el token.
// No se manda profesional_id desde frontend.
export function guardarConfiguracionCalendarioAction({ config, payload }) {
    return async (dispatch) => {
        dispatch(actualizarConfiguracionCalendario());

        try {
            const normalizedPayload = normalizeCalendarViewSettings(payload);

            if (!normalizedPayload) {
                throw new Error('La configuración del calendario no es válida.');
            }

            const respuesta = await clienteAxios.post(
                '/usuarios/configuracioncalendario',
                normalizedPayload,
                config
            );

            const configuracion = normalizeCalendarViewSettings(
                respuesta?.data?.configuracion || normalizedPayload
            );

            if (!configuracion) {
                throw new Error('El backend no devolvió una configuración válida después de guardar.');
            }

            dispatch(actualizarConfiguracionCalendarioExito(configuracion));

            return configuracion;

        } catch (error) {
            const mensaje = getBackendErrorMessage(
                error,
                'No se pudo guardar la configuración del calendario.'
            );

            const errores = { msg: mensaje };

            dispatch(mostrarAlerta(errores));

            const customError = new Error(mensaje);
            customError.userMessage = mensaje;
            customError.originalError = error;

            throw customError;
        }
    };
}

// Marca que se solicitó cargar la configuración del calendario.
const cargarConfiguracionCalendario = () => ({
    type: CARGAR_CONFIGURACION_CALENDARIO,
    payload: true
});

// Guarda en Redux la configuración cargada desde backend.
const cargarConfiguracionCalendarioExito = (configuracion) => ({
    type: CARGAR_CONFIGURACION_CALENDARIO_EXITO,
    payload: configuracion
});

// Marca que se solicitó actualizar la configuración del calendario.
const actualizarConfiguracionCalendario = () => ({
    type: ACTUALIZAR_CONFIGURACION_CALENDARIO,
    payload: true
});

// Guarda en Redux la configuración actualizada desde backend.
const actualizarConfiguracionCalendarioExito = (configuracion) => ({
    type: ACTUALIZAR_CONFIGURACION_CALENDARIO_EXITO,
    payload: configuracion
});

// Guarda la alerta en Redux, siguiendo la estructura que ya usás.
const mostrarAlerta = (alerta) => ({
    type: MOSTRAR_ALERTA,
    payload: alerta
});